import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate } from "ra-core";
import { AlertCircle, CheckCircle2, MessageCircle, Send } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { CrmDataProvider } from "../providers/types";
import type {
  Contact,
  WhatsAppOutboundMessage,
  WhatsAppTemplate,
} from "../types";
import {
  senderCanSend,
  senderLabel,
  templateMatchesSender,
} from "../whatsapp/whatsappSenderSelection";
import {
  MAX_WHATSAPP_TEXT_LENGTH,
  WHATSAPP_MESSAGING_QUERY_PREFIX,
  blockReasonText,
  useSendWhatsAppMessage,
  type SendWhatsAppOutcome,
} from "./useSendWhatsAppMessage";
import { WhatsAppMarketingOptOutNotice } from "./WhatsAppMarketingOptOutNotice";

type ComposeMode = "freeform" | "template";

export const ContactWhatsAppActions = ({
  contact,
  compact = false,
  open,
  onOpenChange,
  showTrigger = true,
  keepOpen = false,
  onSent,
}: {
  contact: Contact;
  compact?: boolean;
  /** Controlled open state (uncontrolled when omitted). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the "Send WhatsApp" trigger button (e.g. when opened programmatically). */
  showTrigger?: boolean;
  /** Keep the inbox episode open after sending instead of auto-resolving it. */
  keepOpen?: boolean;
  onSent?: (outcome: Extract<SendWhatsAppOutcome, { status: "sent" }>) => void;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setOpen = (next: boolean) => {
    setInternalOpen(next);
    onOpenChange?.(next);
  };

  const send = useSendWhatsAppMessage({
    contactId: contact.id,
    enabled: isOpen,
  });
  const [body, setBody] = useState("");
  const [modeOverride, setModeOverride] = useState<ComposeMode | null>(null);
  const [templateId, setTemplateId] = useState("");
  const [templateParameters, setTemplateParameters] = useState<
    Record<string, string>
  >({});

  const templatesQuery = useQuery({
    queryKey: [WHATSAPP_MESSAGING_QUERY_PREFIX, "templates"],
    queryFn: () => dataProvider.listWhatsAppTemplates(),
    enabled: isOpen,
    staleTime: 60_000,
  });
  const messagesQuery = useQuery({
    queryKey: [WHATSAPP_MESSAGING_QUERY_PREFIX, "outbound", String(contact.id)],
    queryFn: () => dataProvider.getContactOutboundMessages(contact.id),
    enabled: isOpen,
    staleTime: 15_000,
  });
  const templates = useMemo(
    () =>
      (templatesQuery.data ?? []).filter(
        (template) => template.send_eligible !== false,
      ),
    [templatesQuery.data],
  );
  const messages: WhatsAppOutboundMessage[] = messagesQuery.data ?? [];
  const loading = templatesQuery.isFetching || messagesQuery.isFetching;

  const { capability, selectedSender, senderReady, sending } = send;
  const charactersRemaining = MAX_WHATSAPP_TEXT_LENGTH - body.length;
  const availableTemplates = useMemo(
    () =>
      templates.filter((template) =>
        templateMatchesSender(template, selectedSender),
      ),
    [selectedSender, templates],
  );
  const selectedTemplate =
    availableTemplates.find((template) => String(template.id) === templateId) ??
    availableTemplates[0];
  // Freeform is only offered while the 24h window is open; otherwise the
  // dialog is pinned to templates regardless of the operator's last choice.
  const mode: ComposeMode =
    capability && !capability.can_send_freeform
      ? "template"
      : (modeOverride ?? "freeform");
  const canSubmit =
    senderReady &&
    !sending &&
    (mode === "freeform"
      ? !!capability?.can_send_freeform &&
        body.trim().length > 0 &&
        body.length <= MAX_WHATSAPP_TEXT_LENGTH
      : !!selectedTemplate &&
        templateFieldsComplete(selectedTemplate, templateParameters));

  const statusLabel = useMemo(() => {
    if (!capability) return translate("crm.whatsapp.loading");
    if (capability.can_send_freeform) {
      return translate("crm.whatsapp.freeform_available");
    }
    return translate("crm.whatsapp.template_required");
  }, [capability, translate]);

  const handleSend = async () => {
    if (mode === "template") {
      if (!selectedTemplate) return;
      const outcome = await send.sendTemplate(
        { template: selectedTemplate, parameters: templateParameters },
        { keepOpen },
      );
      if (outcome.status !== "sent") return;
      setTemplateParameters({});
      void messagesQuery.refetch();
      onSent?.(outcome);
      return;
    }
    const outcome = await send.sendText(body, { keepOpen });
    if (outcome.status === "blocked") {
      setModeOverride("template");
      return;
    }
    if (outcome.status !== "sent") return;
    setBody("");
    void messagesQuery.refetch();
    onSent?.(outcome);
  };

  const refresh = () => {
    void send.refresh();
    void templatesQuery.refetch();
    void messagesQuery.refetch();
  };

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      {showTrigger ? (
        <DialogTrigger asChild>
          <Button
            type="button"
            variant={compact ? "outline" : "secondary"}
            size={compact ? "sm" : "default"}
            className="gap-2"
          >
            <MessageCircle className="size-4" />
            {translate("crm.whatsapp.action")}
          </Button>
        </DialogTrigger>
      ) : null}
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{translate("crm.whatsapp.title")}</DialogTitle>
          <DialogDescription className="sr-only">
            {translate("crm.whatsapp.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge
              variant={capability?.can_send_freeform ? "default" : "secondary"}
            >
              {statusLabel}
            </Badge>
            {capability?.whatsapp_user_id && (
              <span className="text-muted-foreground">
                {capability.whatsapp_user_id}
              </span>
            )}
          </div>

          <WhatsAppMarketingOptOutNotice capability={capability} />

          {capability && !capability.can_send_freeform && (
            <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <span>
                {blockReasonText(capability.block_reasons, translate)}
              </span>
            </div>
          )}

          {capability?.can_send_freeform &&
            capability.freeform_window_expires_at && (
              <div className="flex gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
                <span>
                  {translate("crm.whatsapp.window_expires", {
                    time: formatDateTime(capability.freeform_window_expires_at),
                  })}
                </span>
              </div>
            )}

          <div className="space-y-2">
            <Label>Sender</Label>
            <Select value={send.senderKey} onValueChange={send.setSenderKey}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a ready sender" />
              </SelectTrigger>
              <SelectContent>
                {(send.senders?.senders ?? []).map((sender) => (
                  <SelectItem
                    key={sender.sender_key}
                    value={sender.sender_key}
                    disabled={!senderCanSend(sender, "direct")}
                  >
                    {senderLabel(sender)} · {sender.lifecycle}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {send.senders && !senderReady && (
              <p className="text-xs text-destructive">
                No verified sender is selected.
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant={mode === "freeform" ? "default" : "outline"}
              size="sm"
              disabled={!capability?.can_send_freeform}
              onClick={() => setModeOverride("freeform")}
            >
              {translate("crm.whatsapp.freeform_mode")}
            </Button>
            <Button
              type="button"
              variant={mode === "template" ? "default" : "outline"}
              size="sm"
              onClick={() => setModeOverride("template")}
            >
              {translate("crm.whatsapp.template_mode")}
            </Button>
          </div>

          {mode === "freeform" ? (
            <div className="space-y-2">
              <Textarea
                value={body}
                onChange={(event) => setBody(event.target.value)}
                disabled={!capability?.can_send_freeform || sending || loading}
                placeholder={translate("crm.whatsapp.placeholder")}
                rows={6}
              />
              <div className="text-right text-xs text-muted-foreground">
                {charactersRemaining}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>{translate("crm.whatsapp.template")}</Label>
                <Select
                  value={selectedTemplate ? String(selectedTemplate.id) : ""}
                  onValueChange={setTemplateId}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTemplates.map((template) => (
                      <SelectItem
                        key={String(template.id)}
                        value={String(template.id)}
                      >
                        {template.name} ({template.language})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedTemplate?.body_text && (
                <div className="rounded-md border bg-muted/30 p-3 text-sm">
                  {selectedTemplate.body_text}
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                {(selectedTemplate?.parameter_schema ?? []).map((field) => (
                  <div className="space-y-2" key={field.key}>
                    <Label>{field.label || field.key}</Label>
                    <Input
                      value={templateParameters[field.key] ?? ""}
                      onChange={(event) =>
                        setTemplateParameters({
                          ...templateParameters,
                          [field.key]: event.target.value,
                        })
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <Separator />

          <div className="space-y-2">
            <h3 className="text-sm font-medium">
              {translate("crm.whatsapp.recent_messages")}
            </h3>
            {messages.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {translate("crm.whatsapp.no_messages")}
              </p>
            ) : (
              <div className="max-h-48 space-y-2 overflow-auto pr-1">
                {messages.map((message) => (
                  <OutboundMessageRow
                    key={String(message.id)}
                    message={message}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={refresh}
            disabled={loading || sending}
          >
            {translate("ra.action.refresh")}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSend()}
            disabled={!canSubmit}
          >
            <Send className="size-4" />
            {sending
              ? translate("crm.whatsapp.sending")
              : translate("crm.whatsapp.send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const OutboundMessageRow = ({
  message,
}: {
  message: WhatsAppOutboundMessage;
}) => (
  <div className="rounded-md border p-3">
    <div className="mb-1 flex items-center justify-between gap-2">
      <Badge
        variant={
          message.provider_status === "failed" ? "destructive" : "secondary"
        }
      >
        {message.provider_status}
      </Badge>
      <span className="text-xs text-muted-foreground">
        {formatDateTime(message.created_at)}
      </span>
    </div>
    <p className="line-clamp-3 text-sm">
      {message.body || message.template_name}
    </p>
    {message.sender_key && (
      <p className="mt-1 font-mono text-xs text-muted-foreground">
        {message.sender_key}
      </p>
    )}
    {message.provider_error_details && (
      <p className="mt-1 text-xs text-destructive">
        {message.provider_error_details}
      </p>
    )}
  </div>
);

function templateFieldsComplete(
  template: WhatsAppTemplate,
  values: Record<string, string>,
) {
  return (template.parameter_schema ?? []).every(
    (field) => !field.required || values[field.key]?.trim(),
  );
}

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
